import React from 'react';
import { Calendar, FileText, History } from 'lucide-react';

import { useAuth } from '@/features/auth/AuthContext';
import { getAuthRuntimeSessionIdentity } from '@/features/auth/services/authRuntime';
import { getAdminViewAsUserId } from '@/features/auth/services/adminViewAs';
import { authService } from '@/features/auth/services/authService';
import { useProjectData } from '@/features/projects/ProjectDataContext';
import { getNodeId, getProjectIdFromNodeId, useMultiProject } from '@/features/projects/MultiProjectContext';
import { NodeMetadataEditor } from '@/features/projects/components/NodeMetadataEditor';
import { getVisibilityActionOptions } from '@/features/projects/utils/visibilityAuthority';
import { approvalRequestService } from '@/features/projects/services/approvalRequestService';
import { useProjectRoleCheck } from '@/features/gantt/hooks/useProjectRoleCheck';
import { nodeMetadataService } from '@/features/projects/services/nodeMetadataService';
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import { ProjectNode } from '@/shared/types/projectData';
import { useModalEscape } from '@/shared/services/KeyboardEventManager';
import { isMeetingRecord } from '@/shared/utils/documentRecordClassification';
import { cn, useTheme } from '@/ui/theme/ThemeContext';
import { noteAutosaveCoordinator } from '../../services/noteAutosave';

import { TaskDetailsPanelProps } from './TaskDetailsPanelTypes';
import { getSuggestions } from './TaskDetailsPanelHelpers';
import { TypeSelector, DescriptionSection, TaggedPeopleSection, TagsSection } from './TaskDetailsPanelSections';
import { FileAttachmentSection } from './sections/FileAttachmentSection';
import { ModalWrapper, PanelContent, PanelHeader } from './TaskDetailsPanelLayout';
import { useModalAnimation } from './TaskDetailsPanelHooks';
import { changeScheduleDay, scheduleDateInputValue } from './scheduleDateInput';
import { parseServerDateTime } from '@/shared/utils/serverTime';

type MetadataEditorTheme = Pick<
  ReturnType<typeof useTheme>['theme'],
  'bgPrimary' | 'bgSecondary' | 'bgTertiary' | 'textPrimary' | 'textSecondary' | 'textMuted' | 'borderPrimary' | 'accentBlue'
>;
type BufferedNoteTitle = { owner: string; value: string; previous: string; commit: (value: string) => void };
type NoteTagInput = { owner: string; value: string; tags: string[] };
const sameTags = (left: string[] | undefined, right: string[]): boolean =>
  (left?.length || 0) === right.length && right.every((tag, index) => tag === left?.[index]);

const formatAuditTimestamp = (
  value: unknown,
  locale: string | undefined,
  unknownTimeLabel: string,
): string => {
  if (typeof value !== 'string' || !value.trim()) {
    return unknownTimeLabel;
  }

  // Node audit timestamps arrive as offset-less UTC; a bare date stays on its calendar day.
  const parsed = parseServerDateTime(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: value.includes('T') ? '2-digit' : undefined,
    minute: value.includes('T') ? '2-digit' : undefined,
  }).format(parsed);
};

export const NoteDetailsPanel: React.FC<TaskDetailsPanelProps> = ({
  task,
  onClose,
}) => {
  const taskRef = React.useRef(task);
  taskRef.current = task;
  const { theme } = useTheme();
  const { t, locale } = useSettings();
  const { projectData, applyOp } = useProjectData();
  const {
    availableProjects,
    getTaggableUsers,
    getAssigneeDisplayName,
  } = useMultiProject();
  const { user, actualUser } = useAuth();
  const { canEditProject } = useProjectRoleCheck({ availableProjects, user, nodes: projectData?.nodes || [] });
  const { isOpen, modalHeight, modalWidth, modalRef } = useModalAnimation();
  const inferredTaskProjectId = getProjectIdFromNodeId(task.id || null);
  const taskProjectId = task.projectId
    || (inferredTaskProjectId && inferredTaskProjectId !== 'default' ? inferredTaskProjectId : undefined)
    || projectData?.projectId || '';
  const renderedSession = getAuthRuntimeSessionIdentity();
  const renderedViewAs = getAdminViewAsUserId();
  const actorId = actualUser?.id || authService.getUser()?.id || user?.id || '';
  const noteIdentity = JSON.stringify([actorId, user?.id || '', renderedViewAs, taskProjectId, getNodeId(task.id || '')]);
  const subscribeDraft = React.useCallback((listener: () => void) => noteAutosaveCoordinator.subscribe(noteIdentity, listener), [noteIdentity]);
  const getDraft = React.useCallback(() => noteAutosaveCoordinator.snapshot(noteIdentity), [noteIdentity]);
  const draft = React.useSyncExternalStore(subscribeDraft, getDraft, getDraft);
  const [editedNote, setEditedNote] = React.useState<Partial<ProjectNode>>(() => ({ ...task, ...draft.changes, ...(draft.titleDraft ? { title: draft.titleDraft.value } : {}), type: 'document' }));
  const editedNoteRef = React.useRef(editedNote);
  editedNoteRef.current = editedNote;
  const titleDirty = Boolean(draft.titleDraft);
  const titleDraftRef = React.useRef<BufferedNoteTitle | null>(null);
  const hasChanges = draft.status !== 'idle' || titleDirty;
  const saveError = draft.status === 'failed' ? t('Failed to save') : null;
  const isMountedRef = React.useRef(true);
  const noteOwnerRef = React.useRef(noteIdentity);
  const [metadataSuggestionRationale, setMetadataSuggestionRationale] = React.useState<string | null>(null);
  const [isSuggestingMetadata, setIsSuggestingMetadata] = React.useState(false);
  const [tagInput, setTagInput] = React.useState<NoteTagInput | null>(null);
  const tagInputIsCurrent = tagInput?.owner === noteIdentity && sameTags(editedNote.tags, tagInput.tags);
  const tagInputValue = tagInputIsCurrent ? tagInput.value : (editedNote.tags || []).join(', ');

  React.useEffect(() => {
    // A note/account switch, undo, or different canonical tags ends this buffer.
    // A matching save echo must not remove a comma or space being typed.
    if (tagInput && !tagInputIsCurrent) setTagInput(null);
  }, [tagInput, tagInputIsCurrent]);

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  React.useEffect(() => {
    noteOwnerRef.current = noteIdentity;
    const stored = noteAutosaveCoordinator.snapshot(noteIdentity);
    const restored = { ...taskRef.current, ...stored.changes, ...(stored.titleDraft ? { title: stored.titleDraft.value } : {}), type: 'document' as const };
    editedNoteRef.current = restored;
    setEditedNote(restored);
    setMetadataSuggestionRationale(null);
    setIsSuggestingMetadata(false);
  }, [noteIdentity]);

  React.useEffect(() => {
    if (!editedNote.id || !projectData?.nodes || hasChanges) {
      return;
    }

    const refreshed = projectData.nodes.find((node) => node.id === editedNote.id);
    if (refreshed) {
      setEditedNote({ ...refreshed, type: 'document' });
    }
  }, [editedNote.id, hasChanges, projectData?.nodes]);

  React.useEffect(() => {
    if (noteOwnerRef.current === noteIdentity && (Object.keys(draft.changes).length || draft.titleDraft)) {
      setEditedNote(current => ({ ...current, ...draft.changes, ...(draft.titleDraft ? { title: draft.titleDraft.value } : {}), type: 'document' }));
    }
  }, [draft.changes, draft.titleDraft, noteIdentity]);

  const commitTitle = React.useCallback((pending: BufferedNoteTitle) => {
    if (noteAutosaveCoordinator.snapshot(pending.owner).titleDraft?.value === pending.value) pending.commit(pending.value);
  }, []);

  const flushTitle = React.useCallback((owner?: string) => {
    const pending = titleDraftRef.current;
    if (!pending || (owner && pending.owner !== owner)) return;
    titleDraftRef.current = null;
    commitTitle(pending);
  }, [commitTitle]);

  React.useEffect(() => () => {
    const pending = titleDraftRef.current;
    // React's effect replay is not a user editing boundary. Capture the old
    // owner's buffer so a real note switch still flushes that exact draft.
    queueMicrotask(() => {
      if (pending?.owner === noteIdentity && (!isMountedRef.current || noteOwnerRef.current !== noteIdentity)) {
        if (titleDraftRef.current === pending) titleDraftRef.current = null;
        commitTitle(pending);
      }
    });
  }, [commitTitle, noteIdentity]);

  const inferredProjectIdFromNode = getProjectIdFromNodeId(editedNote.id || null);
  const noteProjectId =
    editedNote.projectId ||
    (inferredProjectIdFromNode && inferredProjectIdFromNode !== 'default' ? inferredProjectIdFromNode : undefined) ||
    projectData?.projectId;
  const canEditNote = !!noteProjectId && canEditProject(noteProjectId, editedNote, 'update');
  const isAdmin = user?.role === 'admin';
  const canViewInternalMetadata = (user?.role === 'admin' || user?.role === 'employee') && canEditNote;
  const mentionUsers = getTaggableUsers(noteProjectId);
  const mentionLabels = (editedNote.mentions || []).map((mention) => getAssigneeDisplayName(mention));
  const visibilityActionOptions = React.useMemo(
    () => getVisibilityActionOptions({
      role: user?.role,
      currentVisibilityTier: editedNote.visibilityTier,
      isCreate: !editedNote.id,
    }),
    [editedNote.id, editedNote.visibilityTier, user?.role],
  );
  const noteIsMeeting = isMeetingRecord(editedNote);
  const auditActor = typeof editedNote.updatedBy === 'string' && editedNote.updatedBy.trim()
    ? editedNote.updatedBy.trim()
    : typeof editedNote.createdBy === 'string' && editedNote.createdBy.trim()
      ? editedNote.createdBy.trim()
      : '';
  const auditActorLabel = auditActor ? getAssigneeDisplayName(auditActor) || auditActor : t('Unknown');
  const auditTimeLabel = formatAuditTimestamp(
    editedNote.updatedAt || editedNote.createdAt,
    locale,
    t('Unknown time'),
  );

  const handleClose = React.useCallback(() => {
    flushTitle(noteIdentity);
    onClose();
  }, [flushTitle, noteIdentity, onClose]);

  useModalEscape('note-details-panel', handleClose, {
    priority: 260,
    enabled: isOpen,
  });

  const writeNote = React.useCallback(async (changes: Partial<ProjectNode>) => {
    const assertOwner = () => {
      if (!user?.id || !canEditNote || getAuthRuntimeSessionIdentity() !== renderedSession || getAdminViewAsUserId() !== renderedViewAs) {
        throw new Error('Session changed before note save');
      }
    };
    assertOwner();
    await applyOp({
      type: 'update', nodeId: task.id!, data: changes,
      metadata: { projectId: taskProjectId, persistence: 'document-http' },
    });
  }, [applyOp, canEditNote, renderedSession, renderedViewAs, task.id, taskProjectId, user?.id]);

  React.useEffect(() => {
    const stored = noteAutosaveCoordinator.snapshot(noteIdentity).titleDraft;
    if (!stored || titleDraftRef.current?.owner === noteIdentity) return;
    const baseline = { ...taskRef.current, title: stored.previous };
    titleDraftRef.current = { owner: noteIdentity, ...stored, commit: title => {
      noteAutosaveCoordinator.enqueue(noteIdentity, { title, type: 'document' }, baseline, writeNote);
    } };
  }, [noteIdentity, writeNote]);

  const handleBatchFieldChanges = React.useCallback((changes: Partial<ProjectNode>) => {
    if (!task.id || !taskProjectId || !canEditNote || noteOwnerRef.current !== noteIdentity) return;
    const safeChanges: Partial<ProjectNode> = { ...changes, type: 'document' };
    const previous = editedNoteRef.current;
    editedNoteRef.current = { ...previous, ...safeChanges };
    setEditedNote(editedNoteRef.current);
    noteAutosaveCoordinator.enqueue(noteIdentity, safeChanges, previous, writeNote);
  }, [canEditNote, noteIdentity, task.id, taskProjectId, writeNote]);

  const handleFieldChange = React.useCallback(<K extends keyof ProjectNode>(field: K, value: ProjectNode[K]) => {
    handleBatchFieldChanges({ [field]: value } as Partial<ProjectNode>);
  }, [handleBatchFieldChanges]);

  const handleTagsChange = React.useCallback((value: string) => {
    if (!canEditNote || noteOwnerRef.current !== noteIdentity) return;
    const tags = value.split(',').map(tag => tag.trim()).filter(Boolean);
    setTagInput({ owner: noteIdentity, value, tags });
    if (!sameTags(editedNoteRef.current.tags, tags)) handleFieldChange('tags', tags);
  }, [canEditNote, handleFieldChange, noteIdentity]);

  const handleTitleChange = React.useCallback((value: string) => {
    if (!canEditNote || noteOwnerRef.current !== noteIdentity) return;
    const previous = titleDraftRef.current?.owner === noteIdentity
      ? titleDraftRef.current.previous : editedNoteRef.current.title || '';
    // Capture the originating note and writer now; teardown must not use a new note's closure.
    const baseline = { ...editedNoteRef.current, title: previous };
    titleDraftRef.current = { owner: noteIdentity, value, previous, commit: title => {
      noteAutosaveCoordinator.enqueue(noteIdentity, { title, type: 'document' }, baseline, writeNote);
    } };
    editedNoteRef.current = { ...editedNoteRef.current, title: value };
    setEditedNote(editedNoteRef.current);
    noteAutosaveCoordinator.stageTitle(noteIdentity, { value, previous });
  }, [canEditNote, noteIdentity, writeNote]);

  const handleTitleCancel = React.useCallback(() => {
    const pending = titleDraftRef.current;
    if (!pending || pending.owner !== noteIdentity) return;
    titleDraftRef.current = null;
    noteAutosaveCoordinator.stageTitle(noteIdentity, null);
    editedNoteRef.current = { ...editedNoteRef.current, title: pending.previous };
    setEditedNote(editedNoteRef.current);
  }, [noteIdentity]);

  const handleUndo = React.useCallback(() => {
    if (!canEditNote) return;
    setTagInput(null);
    handleTitleCancel();
    const before = noteAutosaveCoordinator.snapshot(noteIdentity).before;
    editedNoteRef.current = { ...editedNoteRef.current, ...before, type: 'document' };
    setEditedNote(editedNoteRef.current);
    noteAutosaveCoordinator.undo(noteIdentity, writeNote);
  }, [canEditNote, handleTitleCancel, noteIdentity, writeNote]);

  const handleRetry = React.useCallback(() => {
    if (canEditNote) noteAutosaveCoordinator.retry(noteIdentity, writeNote);
  }, [canEditNote, noteIdentity, writeNote]);

  const handleSuggestMetadata = React.useCallback(async () => {
    const projectId = noteProjectId || projectData?.projectId;
    if (!projectId) {
      return;
    }

    const owner = noteIdentity;
    setIsSuggestingMetadata(true);
    try {
      const suggestion = await nodeMetadataService.suggest(projectId, {
        nodeType: 'document',
        title: editedNote.title || '',
        description: editedNote.description || '',
        contentExcerpt: editedNote.content || editedNote.description || '',
        parentId: editedNote.parentId || null,
      });
      if (!isMountedRef.current || noteOwnerRef.current !== owner) return;
      setMetadataSuggestionRationale(suggestion.rationale || null);
      handleBatchFieldChanges({
        workstream: suggestion.workstream,
        developmentRoles: suggestion.development_roles,
        visibilityTier: suggestion.visibility_tier,
        visibleToAccountIds: editedNote.visibleToAccountIds && editedNote.visibleToAccountIds.length > 0
          ? editedNote.visibleToAccountIds
          : [],
        classificationSource: suggestion.classification_source,
        classificationConfidence: suggestion.classification_confidence,
      });
    } catch (error) {
      if (!isMountedRef.current || noteOwnerRef.current !== owner) return;
      setMetadataSuggestionRationale(error instanceof Error ? error.message : t('Failed to suggest metadata'));
    } finally {
      if (isMountedRef.current && noteOwnerRef.current === owner) setIsSuggestingMetadata(false);
    }
  }, [
    noteIdentity,
    editedNote.content,
    editedNote.description,
    editedNote.parentId,
    editedNote.title,
    editedNote.visibleToAccountIds,
    handleBatchFieldChanges,
    noteProjectId,
    projectData?.projectId,
    t,
  ]);

  return (
    <ModalWrapper
      focusOwnerId="note-details-panel"
      isOpen={isOpen}
      onClose={handleClose}
      modalRef={modalRef}
      modalHeight={modalHeight}
      modalWidth={modalWidth}
      theme={theme}
    >
      <div data-testid="note-details-panel">
        <PanelHeader
          title={editedNote.title || ''}
          hasChanges={hasChanges}
          onTitleChange={handleTitleChange}
          onTitleCommit={() => flushTitle(noteIdentity)}
          onTitleCancel={handleTitleCancel}
          onUndo={handleUndo}
          editable={canEditNote}
          theme={theme}
        />
        {saveError && (
          <div role="alert" className={cn('px-4 text-sm', theme.accentRed)}>
            {saveError}
            {canEditNote && <button type="button" onClick={handleRetry} className="ml-3 underline">{t('Retry')}</button>}
          </div>
        )}

        <PanelContent>
          <TypeSelector
            itemType="document"
            availableTypes={['document']}
            onTypeChange={() => undefined}
            theme={theme}
          />

          <DescriptionSection
            description={editedNote.description || editedNote.content || ''}
            onDescriptionChange={(value, mentionIds) =>
              handleBatchFieldChanges({ description: value, content: value, mentions: mentionIds })
            }
            mentionUsers={mentionUsers}
            readOnly={!canEditNote}
            theme={theme}
          />

          <section className="space-y-3" data-testid="note-schedule-section">
            <label className={cn('flex items-center gap-2 text-sm font-medium', theme.textSecondary)}>
              <Calendar size={16} />
              {noteIsMeeting ? t('Meeting Schedule') : t('Note Schedule')}
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className="space-y-2">
                <span className={cn('text-xs font-medium', theme.textSecondary)}>
                  {noteIsMeeting ? t('Meeting Start') : t('Note Start')}
                </span>
                <input
                  type="date"
                  data-testid="note-start-date-input"
                  value={scheduleDateInputValue(editedNote.startDate)}
                  onChange={(event) => handleFieldChange('startDate', changeScheduleDay(editedNoteRef.current.startDate, event.target.value))}
                  readOnly={!canEditNote}
                  className={cn(
                    'w-full rounded-lg border px-3 py-2 text-sm',
                    theme.bgSecondary,
                    theme.borderPrimary,
                    theme.textPrimary,
                    theme.focusRing,
                    !canEditNote && 'cursor-default opacity-70',
                  )}
                />
              </label>
              <label className="space-y-2">
                <span className={cn('text-xs font-medium', theme.textSecondary)}>
                  {noteIsMeeting ? t('Meeting End') : t('Note End')}
                </span>
                <input
                  type="date"
                  data-testid="note-end-date-input"
                  value={scheduleDateInputValue(editedNote.endDate)}
                  onChange={(event) => handleFieldChange('endDate', changeScheduleDay(editedNoteRef.current.endDate, event.target.value))}
                  readOnly={!canEditNote}
                  className={cn(
                    'w-full rounded-lg border px-3 py-2 text-sm',
                    theme.bgSecondary,
                    theme.borderPrimary,
                    theme.textPrimary,
                    theme.focusRing,
                    !canEditNote && 'cursor-default opacity-70',
                  )}
                />
              </label>
            </div>
          </section>

          <TaggedPeopleSection
            mentions={editedNote.mentions || []}
            mentionLabels={mentionLabels}
            readOnly={!canEditNote}
            theme={theme}
          />

          <TagsSection
            // TagsSection joins its display array; one display-only value keeps
            // raw keystrokes intact while editedNote/autosave hold normalized tags.
            tags={canEditNote ? [tagInputValue] : editedNote.tags}
            onTagsChange={handleTagsChange}
            suggestions={getSuggestions('tag', (tagInputValue.split(',').pop() || '').trim(), projectData)}
            readOnly={!canEditNote}
            theme={theme}
          />

          {canViewInternalMetadata && (
            <NodeMetadataEditor
              projectId={noteProjectId}
              nodeId={editedNote.id}
              targetLabel={editedNote.title || t('Note')}
              workstream={editedNote.workstream}
              developmentRoles={editedNote.developmentRoles}
              visibilityTier={editedNote.visibilityTier}
              visibilityOptions={visibilityActionOptions}
              visibleToAccountIds={editedNote.visibleToAccountIds}
              classificationConfidence={editedNote.classificationConfidence}
              rationale={metadataSuggestionRationale}
              readOnly={!canEditNote}
              canManageVisibleToAccounts={isAdmin}
              suggesting={isSuggestingMetadata}
              showApplyToDescendants={false}
              applyToDescendants={false}
              theme={{
                bgPrimary: theme.bgPrimary, bgSecondary: theme.bgSecondary, bgTertiary: theme.bgTertiary,
                textPrimary: theme.textPrimary, textSecondary: theme.textSecondary, textMuted: theme.textMuted,
                borderPrimary: theme.borderPrimary, accentBlue: theme.accentBlue,
              } satisfies MetadataEditorTheme}
              onSuggest={handleSuggestMetadata}
              onWorkstreamChange={(value) => handleBatchFieldChanges({ workstream: value })}
              onDevelopmentRolesChange={(value) => handleBatchFieldChanges({ developmentRoles: value })}
              onVisibilityTierChange={(value) => handleBatchFieldChanges({ visibilityTier: value })}
              onRequestVisibilityTier={async ({ projectId, nodeId, visibilityTier, scope, rationale }) => {
                await approvalRequestService.create({
                  projectId,
                  nodeId,
                  requestedVisibilityTier: visibilityTier,
                  scope,
                  rationale,
                });
              }}
              onVisibleToAccountIdsChange={(value) => handleBatchFieldChanges({ visibleToAccountIds: value })}
            />
          )}

          <section
            className={cn('rounded-xl border p-3', theme.bgSecondary, theme.borderPrimary)}
            data-testid="note-audit-card"
          >
            <div className={cn('mb-2 flex items-center gap-2 text-sm font-semibold', theme.textPrimary)}>
              <History size={15} />
              {t('Version')}
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div>
                <div className={theme.textSecondary}>{t('Last editor')}</div>
                <div className={cn('mt-1 truncate font-medium', theme.textPrimary)}>{auditActorLabel}</div>
              </div>
              <div>
                <div className={theme.textSecondary}>{t('Last changed')}</div>
                <div className={cn('mt-1 truncate font-medium', theme.textPrimary)}>{auditTimeLabel}</div>
              </div>
              <div>
                <div className={theme.textSecondary}>{t('Version')}</div>
                <div className={cn('mt-1 font-medium', theme.textPrimary)}>v{editedNote.version || 1}</div>
              </div>
            </div>
          </section>

          {editedNote.id && noteProjectId && (
            <FileAttachmentSection
              projectId={noteProjectId}
              nodeId={editedNote.id}
              nodeType="document"
              canEdit={canEditNote}
            />
          )}

          <div className={cn('hidden')} data-testid="note-task-fields-hidden">
            <FileText size={1} />
          </div>
        </PanelContent>
      </div>
    </ModalWrapper>
  );
};
